import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import Button from '../components/Button';
import DeployModal from '../components/nfs/DeployModal';
import { ServerIcon, FolderIcon, CloudArrowUpIcon, ClockIcon } from '@heroicons/react/24/outline';
import NFSInstancesList from '../components/nfs/NFSInstancesList';
import NFSInstanceForm from '../components/nfs/NFSInstanceForm';
import SharesList from '../components/nfs/SharesList';
import ShareForm from '../components/nfs/ShareForm';
import { nfsApi } from '../services/api';
import { NFSServer as BaseNFSServer, NFSShare } from '../types';
import sseService from '../services/sseService';

const tabLabels = [
  { label: 'Servers', icon: ServerIcon },
  { label: 'Shares', icon: FolderIcon },
];

type NFSServer = BaseNFSServer & { status?: string; lastChecked?: string };

const NFSPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [servers, setServers] = useState<NFSServer[]>([]);
  const [shares, setShares] = useState<NFSShare[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<NFSServer | null>(null);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isShareFormOpen, setIsShareFormOpen] = useState(false);
  const [editingShare, setEditingShare] = useState<NFSShare | null>(null);
  const [loading, setLoading] = useState({ servers: false, shares: false });

  // Load servers and shares on mount
  useEffect(() => {
    setLoading(l => ({ ...l, servers: true }));
    nfsApi.getAllServers()
      .then(async (fetchedServers) => {
        // Fetch status for each server in parallel
        const serversWithStatus = await Promise.all(
          fetchedServers.map(async (srv) => {
            try {
              const statusResp = await nfsApi.getStatus(srv.id);
              return {
                ...srv,
                status: statusResp.status,
                lastChecked: statusResp.lastChecked,
              };
            } catch (e) {
              return { ...srv, status: 'Stopped', lastChecked: undefined };
            }
          })
        );
        setServers(serversWithStatus);
      })
      .finally(() => setLoading(l => ({ ...l, servers: false })));
    setLoading(l => ({ ...l, shares: true }));
    nfsApi.getAllShares()
      .then(setShares)
      .finally(() => setLoading(l => ({ ...l, shares: false })));
  }, []);

  useEffect(() => {
    // Subscribe to NFS server status updates from shared SSE service
    const handleNfsServerStatus = (data: any) => {
      setServers(prevServers => prevServers.map(srv => {
        if (srv.id === data.id) {
          return {
            ...srv,
            status: data.status,
            lastChecked: data.lastChecked,
          };
        }
        return srv;
      }));
    };
    sseService.on('instanceStatus', handleNfsServerStatus);
    return () => {
      sseService.off('instanceStatus', handleNfsServerStatus);
    };
  }, []);

  // Server CRUD logic
  const handleAddServer = () => {
    setEditingServer(null);
    setIsFormOpen(true);
  };
  const handleEditServer = (id: string) => {
    const srv = servers.find(i => i.id === id);
    if (srv) {
      setEditingServer(srv);
      setIsFormOpen(true);
    }
  };
  const handleDeleteServer = async (id: string) => {
    await nfsApi.deleteServer(id);
    setServers(servers.filter(srv => srv.id !== id));
  };
  const handleSaveServer = async (server: Partial<NFSServer>) => {
    if (server.id) {
      const updated = await nfsApi.updateServer(server.id, server as NFSServer);
      setServers(servers.map(i => i.id === updated.id ? updated : i));
    } else {
      const created = await nfsApi.createServer(server as Omit<NFSServer, 'id'>);
      setServers([...servers, created]);
    }
    setIsFormOpen(false);
    setEditingServer(null);
  };
  const handleCancelForm = () => {
    setIsFormOpen(false);
    setEditingServer(null);
  };

  // Share CRUD logic
  const handleAddShare = () => {
    setEditingShare(null);
    setIsShareFormOpen(true);
  };
  const handleEditShare = (id: string) => {
    const share = shares.find(s => s.id === id);
    if (share) {
      setEditingShare(share);
      setIsShareFormOpen(true);
    }
  };
  const handleDeleteShare = async (id: string) => {
    await nfsApi.deleteShare(id);
    setShares(shares.filter(s => s.id !== id));
  };
  const handleSaveShare = async (share: Partial<NFSShare>) => {
    if (share.id) {
      const updated = await nfsApi.updateShare(share.id, share as NFSShare);
      setShares(shares.map(s => s.id === updated.id ? updated : s));
    } else {
      const created = await nfsApi.createShare(share as Omit<NFSShare, 'id'>);
      setShares([...shares, created]);
    }
    setIsShareFormOpen(false);
    setEditingShare(null);
  };
  const handleCancelShareForm = () => {
    setIsShareFormOpen(false);
    setEditingShare(null);
  };

  // Mock history modal
  const HistoryModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) =>
    isOpen ? (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-gray-900 rounded-lg shadow-lg p-8 w-full max-w-md">
          <h2 className="text-lg font-semibold text-white mb-4">Deployment History</h2>
          <p className="text-gray-300 mb-6">This is a mock deployment history modal for NFS.</p>
          <div className="flex justify-end pt-2">
            <Button variant="primary" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    ) : null;

  // Loading skeleton for tables
  const LoadingTable = ({ columns }: { columns: string[] }) => (
    <div className="flex justify-center items-center py-24 bg-zinc-900/30 rounded-lg border border-zinc-800">
      <div className="flex flex-col items-center">
        <div className="spinner mb-4"></div>
        <p className="text-zinc-400">Loading {columns.join(' / ')}...</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {/* Header */}
      <div className="mb-8">
        <div className="relative shadow-sm">
          <div className="mx-auto px-4 sm:px-6 lg:px-0">
            <div className="flex h-16 items-center justify-between">
              {/* Left: Logo and title */}
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded bg-indigo-500 flex items-center justify-center">
                    <ServerIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h1 className="text-xl font-semibold tracking-tight text-zinc-100">NFS Management</h1>
                  <p className="text-xs text-zinc-400">Enterprise Configuration</p>
                </div>
              </div>
              {/* Right: Status and actions */}
              <div className="flex items-center space-x-4">
                {/* Status indicators */}
                <div className="hidden md:flex items-center space-x-4 border-r border-zinc-700 pr-4">
                  <div className="flex flex-col justify-center items-center px-3 py-1">
                    <div className="text-lg font-semibold text-zinc-100">{servers.length}</div>
                    <div className="text-xs text-zinc-400">Servers</div>
                  </div>
                  <div className="flex flex-col justify-center items-center px-3 py-1 border-l border-zinc-700 pl-4">
                    <div className="flex items-center">
                      {servers.some(i => i.status === 'Running') ? (
                        <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                      ) : (
                        <div className="h-2 w-2 rounded-full bg-red-500 mr-2"></div>
                      )}
                      <span className="text-zinc-100 text-xs font-medium">
                        {servers.some(i => i.status === 'Running') ? 'RUNNING' : 'STOPPED'}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-400">Service Status</div>
                  </div>
                </div>
                {/* Action buttons */}
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsHistoryModalOpen(true)}
                    className="inline-flex items-center rounded-md bg-zinc-800 border border-zinc-700 px-3 py-1.5 text-sm font-medium text-zinc-200 shadow-sm hover:bg-zinc-700 transition-colors duration-150 ease-in-out focus:outline-none"
                  >
                    <ClockIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                    History
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsDeployModalOpen(true)}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 transition-colors duration-150 ease-in-out focus:outline-none"
                  >
                    <CloudArrowUpIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                    Deploy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Tab navigation with polish */}
        <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
          <div className="border-b border-zinc-800/80">
            <div className="mx-auto px-4 sm:px-6 lg:px-0">
              <Tab.List className="flex -mb-px space-x-8">
                {tabLabels.map((tab) => (
                  <Tab
                    key={tab.label}
                    className={({ selected }) =>
                      `group relative py-4 px-1 text-sm font-medium outline-none transition-colors duration-300 \
                      ${selected ? 'text-indigo-400' : 'text-zinc-400 hover:text-zinc-200'}`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <div className="flex items-center">
                          <tab.icon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                          <span className="whitespace-nowrap">{tab.label}</span>
                        </div>
                        <span
                          className={`
                            pointer-events-none absolute left-0 right-0 bottom-0 h-0.5
                            bg-indigo-500 rounded
                            transition-transform duration-300
                            ${selected ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'}
                          `}
                          style={{ transformOrigin: 'center' }}
                        />
                      </>
                    )}
                  </Tab>
                ))}
              </Tab.List>
            </div>
          </div>
          {/* Tab panels with consistent padding */}
          <div className="mx-auto px-4 sm:px-6 lg:px-0 pt-6">
            <Tab.Panels>
              <Tab.Panel>
                {loading.servers ? (
                  <div className="flex justify-center items-center py-24 bg-zinc-900/30 rounded-lg border border-zinc-800">
                    <div className="flex flex-col items-center">
                      <div className="spinner mb-4"></div>
                      <p className="text-zinc-400">Loading servers...</p>
                    </div>
                  </div>
                ) : (
                  <NFSInstancesList
                    instances={servers}
                    onAddInstance={handleAddServer}
                    onEditInstance={handleEditServer}
                    onDeleteInstance={handleDeleteServer}
                  />
                )}
                <NFSInstanceForm
                  isOpen={isFormOpen}
                  onCancel={handleCancelForm}
                  onSave={handleSaveServer}
                  instance={editingServer || undefined}
                />
              </Tab.Panel>
              <Tab.Panel>
                {loading.shares ? (
                  <LoadingTable columns={["Server", "Path", "Clients"]} />
                ) : (
                  <SharesList
                    shares={shares}
                    onAddShare={handleAddShare}
                    onEditShare={handleEditShare}
                    onDeleteShare={handleDeleteShare}
                    servers={servers}
                  />
                )}
                <ShareForm
                  isOpen={isShareFormOpen}
                  onCancel={handleCancelShareForm}
                  onSave={handleSaveShare}
                  share={editingShare || undefined}
                />
              </Tab.Panel>
            </Tab.Panels>
          </div>
        </Tab.Group>
      </div>
      {/* Deploy and History Modals */}
      <DeployModal 
        isOpen={isDeployModalOpen} 
        onClose={() => setIsDeployModalOpen(false)} 
        servers={servers} 
        sseService={sseService} 
        nfsApi={nfsApi} 
      />
      <HistoryModal isOpen={isHistoryModalOpen} onClose={() => setIsHistoryModalOpen(false)} />
    </div>
  );
};

export default NFSPage; 